import { FooterMenu, Loading } from "components"
import { InputSection, Journal, PrintBuildItem } from "./components"
import { DataContext } from "./contexts"
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import moment from "moment"
import toast from "react-hot-toast"
import { useParams } from "react-router-dom"
import type { DataType } from "./types"
import { useApi, useToggle } from "utils"
import { type FormType, useDetailByBuild, validationSchema, checkQtyStatus } from "./utils"
import { PropsWithChildren } from "react"

export default function EditBuildItem(): JSX.Element {
  const { build_id } = useParams()
  const { data, isLoading } = useDetailByBuild(Number(build_id))

  return (
    <section className="container my-3 flex flex-col gap-3">
      {isLoading || !data 
      ? <Loading loading={isLoading} errorText="No data available" />
      : <FormSection data={data} build_id={build_id!} />
      }
    </section>
  )
}

function FormSection(params: PropsWithChildren<FormSectionType>): JSX.Element {
  const product = params.data.product

  const defaultValues: FormType = {
    employee_id: product.employee_id,
    location_id: product.location_id,
    qty_build_unit: product.qty_build_unit,
    trx_code: product.trx_code,
    transaction_date: moment(product.transaction_date).format("YYYY-MM-DD"),
    component_at_cost:product.component_at_cost,
    coa_overhead_id:0,
    total_cac:0,
    total_amount:0,
    overhead:0,
  }

  let disabeld_form = false
  if(product.approve_by !== '-' && product.approve_by !== null){
    disabeld_form = true
  }
  const methods = useForm<FormType>({
    defaultValues,
    disabled: disabeld_form,
    resolver: yupResolver(validationSchema)
  })

  return (
    <FormProvider {...methods}>
      <DataContext.Provider value={{ ...params.data, product: { ...product }}}>
        <InputSection />

        <Footer
          data={params.data.ingredient}
          product={product}
          build_id={params.build_id}
        />
      </DataContext.Provider>
    </FormProvider>
  )
}

function Footer(params: PropsWithChildren<FooterType>): JSX.Element {
  const api = useApi()
  const { isActive, toggle } = useToggle(false)

  const { control, formState, handleSubmit } = useFormContext<FormType>()
  const qty_build_unit = useWatch({ control, name: "qty_build_unit" })
  
  const onSubmit = (value: FormType): Promise<void> => {
    return new Promise<void>((resolve) => {
      toast.promise(
        api.put(
          `/list_build_item/update_data?build_id=${params.build_id}`,
          {
            ...value,
            product_id: params.product.product_id,
            ingredients: params.data.map((item) => ({
              build_detail_id:item.build_detail_id,
              build_id: item.build_id,
              product_ingredient_id: item.product_id,
              qty_ingredient_unit: Number(item.qty )* qty_build_unit,
              mr_unit_id: item.unit_convention_id,
              location_id: Number(value.location_id),
              avg_price:Number(item.avg_price),
              amount: item.qty * qty_build_unit * item.avg_price,
            }))
          }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message
        }
      )
      .then(() => resolve())
      .catch(() => {})
      .finally(() => resolve())
    })
  }

  let isDisabled: number = params.data.reduce((acc, item) => {
    const status = checkQtyStatus(item.qty, qty_build_unit, item.soh, Number(item.avg_price))
    if (!status) { return acc + 1 }
    
    return acc
  }, 0)

  if(params.product.approve_by !== '-' && params.product.approve_by !== null){
    isDisabled = 1
  }
  
  return (
    <section>
      <FooterMenu
        customElement={{ journal: <Journal /> }}
        save={{
          actiontype: "save",
          disabled: Boolean(isDisabled),
          loading: formState.isSubmitting ? "true" : undefined,
          onClick: handleSubmit(onSubmit)
        }}
        print={{ actiontype: "print", onClick: toggle }}
      />

      {isActive ? (<PrintBuildItem toggle={toggle} />) : null}
    </section>
  )
}

/** Type Sections */
type FormSectionType = {
  data: DataType
  build_id:string
}
type FooterType = {
  data: DataType["ingredient"]
  product: DataType["product"]
  build_id:string
}